/**
 * useVersions Hook
 * Manages forecast versions (create, update, delete, lock, publish)
 * Supports month-level locking within a single yearly version
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { ForecastVersion, VersionStatus } from '../types';
import {
  fetchVersions as apiFetchVersions,
  fetchVersionsByYear as apiFetchVersionsByYear,
  createVersion as apiCreateVersion,
  updateVersion as apiUpdateVersion,
  deleteVersion as apiDeleteVersion,
  duplicateVersion as apiDuplicateVersion,
  publishVersion as apiPublishVersion,
  lockVersion as apiLockVersion,
  unlockVersion as apiUnlockVersion,
  setDefaultVersion as apiSetDefaultVersion,
  lockMonth as apiLockMonth,
  unlockMonth as apiUnlockMonth,
  type ForecastVersion as ApiForecastVersion,
  type CreateVersionParams as ApiCreateVersionParams,
  type LockMonthParams as ApiLockMonthParams,
} from '../api';

// ============ Query Keys ============

export const versionQueryKeys = {
  all: ['forecast-v3', 'versions'] as const,
  list: (orgId: number, locationId: number) =>
    [...versionQueryKeys.all, 'list', { orgId, locationId }] as const,
  listByYear: (orgId: number, locationId: number, year: number) =>
    [...versionQueryKeys.all, 'list', { orgId, locationId, year }] as const,
  detail: (versionId: string) =>
    [...versionQueryKeys.all, 'detail', versionId] as const,
};

// ============ Type Transformers ============

/**
 * Transform API response to frontend ForecastVersion type
 */
function transformApiVersion(apiVersion: ApiForecastVersion): ForecastVersion & { isDefault?: boolean } {
  // Debug: log the raw API response to see if roomStatSource is present
  console.log('[useVersions] API version data:', {
    id: apiVersion.id,
    name: apiVersion.name,
    budgetReferenceYear: apiVersion.budgetReferenceYear,
    roomStatSource: apiVersion.roomStatSource,
    actualRollInEnabled: apiVersion.actualRollInEnabled,
    hasRoomStatSource: 'roomStatSource' in apiVersion,
  });

  // Map API status (lowercase) to frontend status (capitalized)
  const statusMap: Record<string, VersionStatus> = {
    draft: 'Draft',
    locked: 'Locked',
    published: 'Published',
  };

  // Use year field, fallback to baselineYear for backward compatibility
  const year = apiVersion.year ?? apiVersion.baselineYear ?? new Date().getFullYear();

  // Generate coverage string (yearly version)
  const coverage = `Jan-Dec ${year}`;

  return {
    id: parseInt(apiVersion.id, 10) || Date.now(), // Frontend expects number
    name: apiVersion.name,
    budgetReferenceYear: apiVersion.budgetReferenceYear,
    description: apiVersion.description ?? undefined,
    type: apiVersion.versionType === 'budget' ? 'Budget' : 'Forecast',
    status: statusMap[apiVersion.status] || 'Draft',
    createdFrom: apiVersion.parentVersionId || null,
    createdAt: apiVersion.createdAt.split('T')[0],
    owner: apiVersion.createdByName || apiVersion.createdBy || 'Unknown',
    coverage,
    orgId: apiVersion.orgId,
    locationId: apiVersion.locationId,
    year,
    versionType: apiVersion.versionType || 'forecast',
    lockedMonths: apiVersion.lockedMonths || [], // Array of locked months
    roomStatSource: apiVersion.roomStatSource || 'pms', // Source for room statistics
    actualRollInEnabled: apiVersion.actualRollInEnabled ?? true,
    startDate: undefined,
    endDate: undefined,
    isDefault: apiVersion.isDefault || false,
  };
}

// ============ Hook Parameters ============

interface UseVersionsParams {
  orgId: number;
  locationId: number;
  year?: number;              // Optional: filter by year
  enabled?: boolean;
}

interface CreateVersionParams {
  name: string;
  description?: string;
  type: 'Forecast' | 'Budget';
  baseVersionId?: string;
  copyOptions?: {
    drivers: boolean;
    assumptions: boolean;
    overrides: boolean;
    notes: boolean;
  };
  year?: number;
}

interface LockMonthParams {
  month: number;              // 1-12
  lockReason?: string;
}

// ============ Return Types ============

interface UseVersionsReturn {
  // Data
  versions: (ForecastVersion & { isDefault?: boolean })[];
  defaultVersionId: number | null;

  // Loading states
  isLoading: boolean;
  error: Error | null;

  // Version mutations
  createVersion: (params: CreateVersionParams) => Promise<ForecastVersion>;
  updateVersionStatus: (versionId: string, status: VersionStatus) => Promise<void>;
  lockVersion: (versionId: string, reason?: string) => Promise<void>;
  unlockVersion: (versionId: string) => Promise<void>;
  publishVersion: (versionId: string, note?: string) => Promise<void>;
  deleteVersion: (versionId: string) => Promise<void>;
  copyVersion: (versionId: string, newName: string) => Promise<ForecastVersion>;
  updateVersion: (versionId: string, updates: { name?: string; description?: string; year?: number }) => Promise<void>;
  setDefaultVersion: (versionId: string) => Promise<void>;

  // Month locking mutations
  lockMonth: (versionId: string, month: number, reason?: string) => Promise<number[]>;
  unlockMonth: (versionId: string, month: number) => Promise<number[]>;

  // Actions
  refetch: () => void;
}

// ============ Main Hook ============

export function useVersions({
  orgId,
  locationId,
  year,
  enabled = true,
}: UseVersionsParams): UseVersionsReturn {
  const queryClient = useQueryClient();
  const currentYear = year || new Date().getFullYear();

  // Fetch versions list from real API
  // If year is provided, fetch by year for monthly versions; otherwise fetch all
  const {
    data: versions = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: year
      ? versionQueryKeys.listByYear(orgId, locationId, year)
      : versionQueryKeys.list(orgId, locationId),
    queryFn: async () => {
      const apiVersions = year
        ? await apiFetchVersionsByYear({ orgId, locationId, year })
        : await apiFetchVersions({ orgId, locationId });
      return apiVersions.map(transformApiVersion);
    },
    enabled: enabled && !!orgId && !!locationId,
    staleTime: 60000, // 1 minute
    refetchOnWindowFocus: false,
  });

  // ============ Mutations ============

  // Helper to invalidate all relevant queries
  const invalidateVersionQueries = () => {
    queryClient.invalidateQueries({ queryKey: versionQueryKeys.list(orgId, locationId) });
    if (year) {
      queryClient.invalidateQueries({ queryKey: versionQueryKeys.listByYear(orgId, locationId, year) });
    }
  };

  const createVersionMutation = useMutation({
    mutationFn: async (params: CreateVersionParams): Promise<ForecastVersion> => {
      const apiParams: ApiCreateVersionParams = {
        orgId,
        locationId,
        name: params.name,
        description: params.description,
        year: params.year || currentYear,
        versionType: params.type === 'Budget' ? 'budget' : 'forecast',
        parentVersionId: params.baseVersionId,
      };
      const newVersion = await apiCreateVersion(apiParams);
      return transformApiVersion(newVersion);
    },
    onSuccess: invalidateVersionQueries,
  });

  // Month locking mutations
  const lockMonthMutation = useMutation({
    mutationFn: async ({ versionId, month, reason }: { versionId: string; month: number; reason?: string }): Promise<number[]> => {
      const result = await apiLockMonth(versionId, { month, lockReason: reason });
      return result.lockedMonths;
    },
    onSuccess: invalidateVersionQueries,
  });

  const unlockMonthMutation = useMutation({
    mutationFn: async ({ versionId, month }: { versionId: string; month: number }): Promise<number[]> => {
      const result = await apiUnlockMonth(versionId, { month });
      return result.lockedMonths;
    },
    onSuccess: invalidateVersionQueries,
  });

  const updateVersionMutation = useMutation({
    mutationFn: async ({ versionId, updates }: { versionId: string; updates: { name?: string; description?: string; year?: number } }) => {
      await apiUpdateVersion(versionId, updates);
    },
    onSuccess: () => {
      // A year change moves the version between per-year lists, so invalidate every list.
      queryClient.invalidateQueries({ queryKey: versionQueryKeys.all });
      invalidateVersionQueries();
    },
  });

  const lockVersionMutation = useMutation({
    mutationFn: async ({ versionId, reason }: { versionId: string; reason?: string }) => {
      await apiLockVersion(versionId, { lockReason: reason });
    },
    onSuccess: invalidateVersionQueries,
  });

  const unlockVersionMutation = useMutation({
    mutationFn: async (versionId: string) => {
      await apiUnlockVersion(versionId);
    },
    onSuccess: invalidateVersionQueries,
  });

  const publishVersionMutation = useMutation({
    mutationFn: async ({ versionId, note }: { versionId: string; note?: string }) => {
      await apiPublishVersion(versionId, note);
    },
    onSuccess: invalidateVersionQueries,
  });

  const deleteMutation = useMutation({
    mutationFn: async (versionId: string) => {
      await apiDeleteVersion(versionId);
    },
    onSuccess: invalidateVersionQueries,
  });

  const duplicateMutation = useMutation({
    mutationFn: async ({ versionId, newName }: { versionId: string; newName: string }): Promise<ForecastVersion> => {
      const duplicated = await apiDuplicateVersion(versionId, newName);
      return transformApiVersion(duplicated);
    },
    onSuccess: invalidateVersionQueries,
  });

  const setDefaultVersionMutation = useMutation({
    mutationFn: async (versionId: string) => {
      await apiSetDefaultVersion(versionId);
    },
    onSuccess: invalidateVersionQueries,
  });

  // ============ Action Handlers ============

  const createVersion = async (params: CreateVersionParams): Promise<ForecastVersion> => {
    return createVersionMutation.mutateAsync(params);
  };

  const updateVersionStatus = async (versionId: string, status: VersionStatus) => {
    if (status === 'Locked') {
      await lockVersionMutation.mutateAsync({ versionId });
    } else if (status === 'Published') {
      await publishVersionMutation.mutateAsync({ versionId });
    }
  };

  const lockVersion = async (versionId: string, reason?: string) => {
    await lockVersionMutation.mutateAsync({ versionId, reason });
  };

  const unlockVersion = async (versionId: string) => {
    await unlockVersionMutation.mutateAsync(versionId);
  };

  const publishVersion = async (versionId: string, note?: string) => {
    await publishVersionMutation.mutateAsync({ versionId, note });
  };

  const deleteVersion = async (versionId: string) => {
    await deleteMutation.mutateAsync(versionId);
  };

  const copyVersion = async (versionId: string, newName: string): Promise<ForecastVersion> => {
    return duplicateMutation.mutateAsync({ versionId, newName });
  };

  const updateVersion = async (versionId: string, updates: { name?: string; description?: string; year?: number }) => {
    await updateVersionMutation.mutateAsync({ versionId, updates });
  };

  const setDefaultVersion = async (versionId: string) => {
    await setDefaultVersionMutation.mutateAsync(versionId);
  };

  // Month locking handlers
  const lockMonth = async (versionId: string, month: number, reason?: string): Promise<number[]> => {
    return lockMonthMutation.mutateAsync({ versionId, month, reason });
  };

  const unlockMonth = async (versionId: string, month: number): Promise<number[]> => {
    return unlockMonthMutation.mutateAsync({ versionId, month });
  };

  // Get the default version ID from versions list
  const defaultVersionId = versions.find(v => v.isDefault)?.id ?? null;

  return {
    // Data
    versions,
    defaultVersionId,

    // Loading states
    isLoading,
    error: error as Error | null,

    // Version mutations
    createVersion,
    updateVersionStatus,
    lockVersion,
    unlockVersion,
    publishVersion,
    deleteVersion,
    copyVersion,
    updateVersion,
    setDefaultVersion,

    // Month locking mutations
    lockMonth,
    unlockMonth,

    // Actions
    refetch,
  };
}

export default useVersions;
